import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiShield, FiLock, FiEye, FiGlobe, FiClock } = FiIcons;

const PrivacyPolicy = () => {
  const lastUpdated = '04 September 2025';

  const sections = [
    {
      title: 'Information we collect',
      icon: FiEye,
      content: [
        {
          subtitle: 'Personal Information',
          items: [
            'Full name and surname',
            'Physical residential address (P.O. Boxes not accepted)',
            'Email address',
            'Phone number',
            'Payment information (credit card details, billing address)',
            'Identity document information as required by South African law',
            'Business registration details (if applicable)'
          ]
        },
        {
          subtitle: 'Technical Information',
          items: [
            'IP addresses and location data',
            'Browser type and version',
            'Operating system information',
            'Website usage statistics',
            'Server logs and access records',
            'Email usage patterns and statistics',
            'Domain registration and DNS records'
          ]
        },
        {
          subtitle: 'Communications',
          items: [
            'Support tickets and correspondence',
            'Email communications',
            'Phone call records (where legally permitted)'
          ]
        }
      ]
    },
    {
      title: 'How we use your information',
      icon: FiLock,
      content: [
        {
          subtitle: 'Service Provision',
          items: [
            'To provide, maintain, and improve our hosting, email, web design, and domain registration services',
            'To process payments and manage billing',
            'To provide customer support and technical assistance',
            'To manage domain registrations and renewals',
            'To monitor service usage and performance'
          ]
        },
        {
          subtitle: 'Legal Compliance',
          items: [
            'To comply with applicable South African laws and regulations',
            'To respond to lawful requests from law enforcement agencies',
            'To comply with court orders and legal processes',
            'To meet regulatory requirements for telecommunications and internet services'
          ]
        },
        {
          subtitle: 'Communication',
          items: [
            'To send service-related notifications and updates',
            'To provide important security alerts',
            'To inform you of policy changes',
            'To send billing and payment reminders'
          ]
        }
      ]
    },
    {
      title: 'Information sharing and disclosure',
      icon: FiGlobe,
      content: [
        {
          subtitle: 'Legal Enforcement',
          items: [
            'We may be legally bound to provide any information required by law',
            'South African law enforcement agencies',
            'Court orders or subpoenas',
            'Regulatory authorities',
            'International legal assistance requests processed through proper legal channels',
            'Emergency situations involving threats to safety'
          ]
        },
        {
          subtitle: 'Service Providers',
          items: [
            'Payment processors',
            'Upstream hosting providers (RackNerd and other infrastructure partners)',
            'Domain registrars and registry operators',
            'Technical support contractors',
            'Legal and professional advisors'
          ]
        },
        {
          subtitle: 'No Other Disclosure',
          items: [
            'We do not sell, rent, or otherwise commercialise your personal information to third parties for marketing purposes'
          ]
        }
      ]
    },
    {
      title: 'Data security',
      icon: FiShield,
      content: [
        {
          subtitle: 'Security Measures',
          items: [
            'Encrypted data transmission (SSL/TLS)',
            'Secure server environments',
            'Access controls and authentication systems',
            'Regular security audits and monitoring',
            'Staff training on data protection'
          ]
        },
        {
          subtitle: 'Data Breach Notification',
          items: [
            'In the event of a data breach that may compromise your personal information, we will notify affected customers and relevant authorities as required by South African law'
          ]
        }
      ]
    },
    {
      title: 'Data retention',
      icon: FiClock,
      content: [
        {
          subtitle: 'Retention Periods',
          items: [
            'Account information: Retained for the duration of your service agreement plus 7 years for legal compliance',
            'Payment records: 5 years as required by South African tax law',
            'Communication records: 3 years from last interaction',
            'Server logs: 12 months unless required for legal proceedings'
          ]
        },
        {
          subtitle: 'Data Deletion',
          items: [
            'Upon service termination, we will delete or anonymise your personal data according to our retention schedule, except where retention is required by law'
          ]
        }
      ]
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Privacy policy
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              How Primehost collects, uses, and protects your personal information
            </p>
            <p className="text-sm text-gray-500">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-lg text-gray-700 leading-relaxed">
              This Privacy Policy describes how Primehost (a division of AdaptSA Business Solutions (Pty) Ltd, 
              a company registered in South Africa) ("we," "us," or "our"), collects, uses, and protects your 
              personal information when you use our web hosting, email hosting, web design, and domain registration services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-sm p-8"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-primary-100 p-3 rounded-xl">
                    <SafeIcon icon={section.icon} className="w-6 h-6 text-primary-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 capitalize">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((subsection, subIndex) => (
                    <div key={subIndex}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {subsection.subtitle}
                      </h3>
                      <ul className="space-y-2">
                        {subsection.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-primary-500 rounded-full mt-2.5 flex-shrink-0"></div>
                            <span className="text-gray-700 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Sections */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Your Rights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your rights</h2>
              <p className="text-gray-700 mb-4">
                Under South African data protection law, you have the right to:
              </p>
              <ul className="space-y-2">
                {[
                  'Access your personal information',
                  'Correct inaccurate information',
                  'Request deletion of your data (subject to legal retention requirements)',
                  'Object to processing in certain circumstances',
                  'Data portability where technically feasible'
                ].map((right, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2.5 flex-shrink-0"></div>
                    <span className="text-gray-700">{right}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, contact us at support@primehost.co.za.
              </p>
            </motion.div>

            {/* International Transfers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">International transfers</h2>
              <p className="text-gray-700 mb-4">
                Some of our service providers may be located outside South Africa. We ensure appropriate 
                safeguards are in place for international data transfers, including:
              </p>
              <ul className="space-y-2">
                {[
                  'Adequacy decisions where applicable',
                  'Standard contractual clauses',
                  'Other legally recognised transfer mechanisms'
                ].map((safeguard, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2.5 flex-shrink-0"></div>
                    <span className="text-gray-700">{safeguard}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-primary-50 rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact information</h2>
              <p className="text-gray-700 mb-4">
                For privacy-related enquiries, contact:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Primehost</strong> (AdaptSA Business Solutions (Pty) Ltd)</p>
                <p>22 Magwa Crescent, Waterfall, Midrand, Gauteng, 1662</p>
                <p>Email: support@primehost.co.za</p>
                <p>Phone: +27 10 007 0690</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Policy Updates */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Changes to this policy
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              We may update this Privacy Policy periodically. We will notify you of material 
              changes by email or through our service announcements. Please review this policy 
              regularly to stay informed about how we protect your information.
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default PrivacyPolicy;